import { COLLISION_CELLS_PER_TILE, type EntityInstance, type JsonSchema, type JsonValue, type ValidationIssue } from "daedalus-core";
import { loadRules } from "../rules/loader.ts";

/**
 * The spawn definition (ADR-082 decision 3): the server-only `creature_spawn` marker a builder places, its template by
 * id, how many creatures it keeps, how long a dead one's slot waits, and the area its creatures spawn and wander in,
 * painted as cells around the marker (the cells field, `[column, rowsUp]` from the bottom-left of the marker's tile).
 * The marker's id is the definition's id. Pure: the editor's Issues list, the compile and the map server read it the
 * same way.
 */

/**
 * The most creatures one definition keeps.
 */
export const MAX_SPAWN_COUNT = 32;

/**
 * The shortest respawn, a second.
 */
export const MIN_RESPAWN_TICKS = 20;

/**
 * The longest respawn, an hour.
 */
export const MAX_RESPAWN_TICKS = 72_000;

const TICKS_PER_SECOND = 20;
const DEFAULT_COUNT = 3;
const DEFAULT_RESPAWN_TICKS = 600;

/**
 * The name of the list a spawn's template is chosen from in the editor (ADR-021's amendment of 2026-10-02): the
 * committed creature templates, which the editor page hands the store under this name.
 */
export const CREATURE_LOOKUP = "creatures";

/**
 * The marker's fields as the editor's inspector draws them; the bounds the schema cannot state are `readSpawnFields`'.
 */
export const CREATURE_SPAWN_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    template: { type: "string", title: "Template", lookup: CREATURE_LOOKUP },
    levelMin: { type: "integer", title: "Lowest", minimum: 1 },
    levelMax: { type: "integer", title: "Highest", minimum: 1 },
    count: { type: "integer", title: "Count", default: DEFAULT_COUNT, minimum: 1 },
    respawn: { type: "integer", title: "Respawn", default: DEFAULT_RESPAWN_TICKS, minimum: MIN_RESPAWN_TICKS, unit: { symbol: "s", per: TICKS_PER_SECOND } },
    area: { type: "cells", title: "Area" },
  },
  required: ["template", "count", "respawn", "area"],
};

/**
 * A spawn definition's fields: the template id, the optional lowest and highest level of its creatures (absent means
 * the template's level; one alone is the level of every creature, both an inclusive range), the count, the respawn in
 * ticks and the area's cells as painted.
 */
export interface SpawnFields {
  readonly template: string;
  readonly levelMin?: number;
  readonly levelMax?: number;
  readonly count: number;
  readonly respawn: number;
  readonly area: readonly (readonly [number, number])[];
}

/**
 * The verdict on one marker's fields; each error is the sentence after the marker's name.
 */
export type SpawnFieldsVerdict = { readonly ok: true; readonly spawn: SpawnFields } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Whether a value is an integer within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function integerIn(value: JsonValue | undefined, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * The build's creature level cap, the creature table's last row, read once.
 */
let defaultLevelCap: number | null = null;

/**
 * The creature level cap of the build's rules, read once.
 *
 * @returns The table's last level.
 */
export function spawnLevelCap(): number {
  defaultLevelCap ??= loadRules().tables.creature_levels.length;
  return defaultLevelCap;
}

/**
 * Which of a spawn marker's level checks fail: each level that is given and is not a whole number from 1 to the cap,
 * and a lowest above a highest. Empty levels pass, and the order is judged only when both levels are in range, so one
 * mistake is reported once.
 */
export interface SpawnLevelChecks {
  readonly levelMin: boolean;
  readonly levelMax: boolean;
  readonly order: boolean;
}

/**
 * Judges a marker's two optional levels, the same rules `readSpawnFields` refuses a marker by, for a form that shows
 * each failure beside its field.
 *
 * @param fields The marker's fields.
 * @param levelCap The most a level may be, the creature table's last row by default.
 * @returns Which checks fail.
 * @example spawnLevelChecks({ levelMin: 5, levelMax: 3 }); // { levelMin: false, levelMax: false, order: true }
 */
export function spawnLevelChecks(fields: { readonly [key: string]: JsonValue }, levelCap: number = spawnLevelCap()): SpawnLevelChecks {
  const lowest = fields["levelMin"];
  const highest = fields["levelMax"];
  const given = (value: JsonValue | undefined): boolean => value !== undefined && value !== null;
  const levelMin = given(lowest) && !integerIn(lowest, 1, levelCap);
  const levelMax = given(highest) && !integerIn(highest, 1, levelCap);
  const order = !levelMin && !levelMax && given(lowest) && given(highest) && (lowest as number) > (highest as number);
  return { levelMin, levelMax, order };
}

/**
 * Checks one optional level field.
 *
 * @param value The field's value; absent or null is empty.
 * @param cap The most allowed.
 * @param name Which field, as the sentence calls it.
 * @param errors Where the error is added.
 * @returns The level, or undefined when it is empty or refused.
 */
function levelField(value: JsonValue | undefined, cap: number, name: string, errors: string[]): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (!integerIn(value, 1, cap)) {
    errors.push(`has a ${name} level of ${String(value)}; use a whole number from 1 to ${cap}`);
    return undefined;
  }
  return value;
}

/**
 * The cells an area value holds, each a pair of integers, or null when it is not a list of them.
 *
 * @param value The value.
 * @returns The cells.
 */
function cellsOf(value: JsonValue | undefined): (readonly [number, number])[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const cells: (readonly [number, number])[] = [];
  for (const pair of value) {
    if (!Array.isArray(pair) || pair.length !== 2 || !Number.isInteger(pair[0]) || !Number.isInteger(pair[1])) {
      return null;
    }
    cells.push([pair[0] as number, pair[1] as number]);
  }
  return cells;
}

/**
 * Reads and checks a spawn marker's fields: a non-empty template id, a count from 1 to `MAX_SPAWN_COUNT`, a respawn
 * from `MIN_RESPAWN_TICKS` to `MAX_RESPAWN_TICKS`, at least one cell of area, and, when given, a lowest and a highest
 * level that are whole numbers from 1 to the cap with the lowest not above the highest.
 *
 * @param fields The marker's fields.
 * @param levelCap The most a level may be, the creature table's last row by default.
 * @returns The definition's fields, or every error in the schema's order.
 */
export function readSpawnFields(fields: { readonly [key: string]: JsonValue }, levelCap: number = spawnLevelCap()): SpawnFieldsVerdict {
  const errors: string[] = [];
  const template = fields["template"];
  if (typeof template !== "string" || template === "") {
    errors.push("names no template; choose a creature template");
  }
  const levelMin = levelField(fields["levelMin"], levelCap, "lowest", errors);
  const levelMax = levelField(fields["levelMax"], levelCap, "highest", errors);
  if (levelMin !== undefined && levelMax !== undefined && levelMin > levelMax) {
    errors.push(`has a lowest level of ${levelMin} above its highest of ${levelMax}; set the lowest at or below the highest`);
  }
  const count = fields["count"];
  if (!integerIn(count, 1, MAX_SPAWN_COUNT)) {
    errors.push(`spawns ${String(count)} creatures; keep its count from 1 to ${MAX_SPAWN_COUNT}`);
  }
  const respawn = fields["respawn"];
  if (!integerIn(respawn, MIN_RESPAWN_TICKS, MAX_RESPAWN_TICKS)) {
    errors.push(`respawns after ${String(respawn)} ticks; set its respawn from ${MIN_RESPAWN_TICKS / TICKS_PER_SECOND} to ${MAX_RESPAWN_TICKS / TICKS_PER_SECOND} seconds (${MIN_RESPAWN_TICKS} to ${MAX_RESPAWN_TICKS} ticks)`);
  }
  const area = cellsOf(fields["area"]);
  if (area === null || area.length === 0) {
    errors.push("has no area; paint at least one cell of ground for its creatures");
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, spawn: { template: template as string, ...(levelMin === undefined ? {} : { levelMin }), ...(levelMax === undefined ? {} : { levelMax }), count: count as number, respawn: respawn as number, area: area as (readonly [number, number])[] } };
}

/**
 * Where a painted area lies in the map's collision cells: each `[column, rowsUp]` counted from the bottom-left corner
 * of the marker's tile, as the editor paints it (`cell-fields.ts`), turned into `[column, row]` of the map's grid.
 *
 * @param x The marker's x in tiles.
 * @param y The marker's y in tiles.
 * @param area The painted cells.
 * @returns The grid cells, in the area's order.
 * @example spawnAreaCells(10, 20, [[0, 0]]); // [[40, 83]], with four collision cells a tile
 */
export function spawnAreaCells(x: number, y: number, area: readonly (readonly [number, number])[]): [number, number][] {
  const column = Math.floor(x * COLLISION_CELLS_PER_TILE);
  const bottom = Math.floor((y + 1) * COLLISION_CELLS_PER_TILE);
  return area.map(([c, r]): [number, number] => [column + c, bottom - 1 - r]);
}

/**
 * The Issues a spawn marker raises in the editor and at a compile, one per bad field, each naming the marker.
 *
 * @param mapId The map.
 * @param layerId The marker's layer.
 * @param entity The marker.
 * @returns The issues.
 */
export function spawnIssues(mapId: string, layerId: string, entity: EntityInstance): ValidationIssue[] {
  const verdict = readSpawnFields(entity.fields);
  return verdict.ok ? [] : verdict.errors.map((message) => ({ mapId, kind: "creature-spawn.field", layerId, entityId: entity.id, message: `creature spawn ${entity.id} ${message}` }));
}
